<template>
  <div class="page px-6 py-4">
    <div class="d-flex align-start justify-space-between flex-wrap mb-4">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">Календарь услуг</h1>
        <div class="text-body-2 grey--text text--darken-1">
          Заводите интервалы доступности экспертов и обрабатывайте записи клиентов.
        </div>
      </div>
      <CalendarStats
        class="mt-2 mt-sm-0"
        :month="currentMonth"
        :summary="monthSummary"
        :experts="experts"
        :services="services"
        @show-calendar="tab = 0"
        @show-appointments="showAppointments"
      />
    </div>

    <v-tabs v-model="tab" color="#483CB9" class="mb-4 calendar-tabs">
      <v-tab>Календарь и слоты</v-tab>
      <v-tab>Команда</v-tab>
      <v-tab>Услуги</v-tab>
      <v-tab>Записи</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <v-tab-item>
        <v-row>
          <v-col cols="12" md="7">
            <MonthGrid
              :month="currentMonth"
              :selected-date="selectedDate"
              :availability="availabilityForMonth(currentMonth)"
              :appointments="appointmentsForMonth(currentMonth)"
              disable-past
              @update:month="setCurrentMonth"
              @select-date="setSelectedDate"
            />
          </v-col>
          <v-col cols="12" md="5">
            <DaySlotsPanel
              :date="selectedDate"
              :availability="availabilityByDate(selectedDate)"
              :appointments="appointmentsByDate(selectedDate)"
              :cancelled-appointments="cancelledAppointmentsByDate(selectedDate)"
              :experts="experts"
              :services="services"
              :service-types="serviceTypes"
              :action-loading="loading.action"
              @add="openCreateAvailability"
              @edit-availability="openEditAvailability"
              @close-availability="onCloseAvailability"
              @accept-appointment="onAcceptAppointment"
              @decline-appointment="onDeclineAppointment"
              @edit-conference-link="onEditConferenceLink"
            />
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item>
        <v-row>
          <v-col cols="12">
            <ExpertsList
              :experts="experts"
              :availability="openAvailability"
              :services="services"
              :service-types="serviceTypes"
              :appointments="appointments"
              :expert-links="expertPublicLinks"
              :service-links="servicePublicLinks"
              :show-links="showLinks"
              :is-admin="isAdmin"
              :my-expert-id="myExpert && myExpert.id"
              @add="openCreateExpert"
              @edit="openEditExpert"
              @delete="onDeleteExpert"
              @create-link="openCreateLink"
              @edit-link="openEditLink"
              @copy-link="copyLink"
              @toggle-link-active="onToggleLinkActive"
              @open-date="openDate"
              @show-appointments="showExpertAppointments"
            />
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item>
        <v-row>
          <v-col cols="12">
            <ServiceTypesManager
              :service-types="serviceTypes"
              :loading="loading.action"
              :service-links="servicePublicLinks"
              :experts="experts"
              :services="services"
              :show-links="showLinks"
              :link-service-type-ids="isAdmin ? null : linkServiceTypeIds"
              @save="onSaveServiceType"
              @toggle-active="onToggleServiceTypeActive"
              @create-link="openCreateLink"
              @edit-link="openEditLink"
              @copy-link="copyLink"
              @toggle-link-active="onToggleLinkActive"
            />
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item>
        <AppointmentsList
          :appointments="appointments"
          :experts="experts"
          :services="services"
          :service-types="serviceTypes"
          :availability="openAvailability"
          :change-expert-errors="errors.changeExpert"
          :change-expert-done="changeExpertDone"
          :action-loading="loading.action"
          :preset="appointmentsPreset"
          @accept-appointment="onAcceptAppointment"
          @decline-appointment="onDeclineAppointment"
          @edit-conference-link="onEditConferenceLink"
          @change-expert="onChangeExpert"
          @clear-change-expert-errors="clearChangeExpertErrors"
        />
      </v-tab-item>
    </v-tabs-items>

    <PublicLinkFormDialog
      v-model="linkDialog.show"
      :kind="linkDialog.kind"
      :link="linkDialog.link"
      :preset-main-id="linkDialog.mainId"
      :experts="experts"
      :services="services"
      :service-types="serviceTypes"
      :locked-expert-id="isAdmin ? null : (myExpert && myExpert.id)"
      :allowed-service-type-ids="isAdmin ? null : linkServiceTypeIds"
      :loading="loading.action"
      :errors="errors.publicLinks"
      @save="onSaveLink"
    />

    <ConfirmDialog
      v-model="disableLinkDialog.show"
      title="Отключить ссылку?"
      text="Гости по этой ссылке больше не смогут записаться. Её можно будет включить снова — адрес не изменится."
      confirm-text="Отключить"
      danger
      :loading="loading.action"
      @confirm="onConfirmDisableLink"
    />

    <v-snackbar v-model="linkCopied" :timeout="2000" color="#483CB9">Ссылка скопирована</v-snackbar>

    <AvailabilityFormDialog
      v-model="availabilityDialog.show"
      :editing-availability="editingAvailability"
      :default-date="selectedDate"
      :experts="experts"
      :services="services"
      :service-types="serviceTypes"
      :loading="loading.action"
      @save="onSaveAvailability"
    />

    <ExpertFormDialog
      v-model="expertDialog.show"
      :editing-expert="expertDialog.expert"
      :services="expertDialog.expert ? servicesByExpert(expertDialog.expert.id) : []"
      :service-types="serviceTypes"
      :loading="loading.action"
      @save="onSaveExpert"
      @upload-photo="onUploadExpertPhoto"
      @delete-photo="onDeleteExpertPhoto"
    />

    <ConfirmDialog
      v-model="declineDialog.show"
      title="Отклонить запись?"
      confirm-text="Отклонить"
      danger
      :loading="loading.action"
      @confirm="onConfirmDecline"
    />

    <ConferenceLinkDialog
      v-model="conferenceLinkDialog.show"
      :initial-url="conferenceLinkDialog.url"
      :loading="loading.action"
      @save="onSaveConferenceLink"
    />

    <StatusModal
      :show="notification.show"
      :success="notification.success"
      :message="notification.message"
      @update:show="SET_NOTIFICATION({ show: $event, success: notification.success, message: notification.message })"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import MonthGrid from '@/components/Calendar/Admin/MonthGrid.vue';
import DaySlotsPanel from '@/components/Calendar/Admin/DaySlotsPanel.vue';
import AvailabilityFormDialog from '@/components/Calendar/Admin/AvailabilityFormDialog.vue';
import ExpertsList from '@/components/Calendar/Admin/ExpertsList.vue';
import ExpertFormDialog from '@/components/Calendar/Admin/ExpertFormDialog.vue';
import ServiceTypesManager from '@/components/Calendar/Admin/ServiceTypesManager.vue';
import ConfirmDialog from '@/components/Calendar/Admin/ConfirmDialog.vue';
import ConferenceLinkDialog from '@/components/Calendar/Admin/ConferenceLinkDialog.vue';
import PublicLinkFormDialog from '@/components/Calendar/Admin/PublicLinkFormDialog.vue';
import CalendarStats from '@/components/Calendar/Admin/CalendarStats.vue';
import AppointmentsList from '@/components/Calendar/Admin/AppointmentsList.vue';
import StatusModal from '@/components/common/StatusModal.vue';

export default {
  name: 'Calendar',
  components: {
    MonthGrid,
    DaySlotsPanel,
    AvailabilityFormDialog,
    ExpertsList,
    ExpertFormDialog,
    ServiceTypesManager,
    ConfirmDialog,
    ConferenceLinkDialog,
    PublicLinkFormDialog,
    CalendarStats,
    AppointmentsList,
    StatusModal,
  },
  data() {
    return {
      tab: 0,
      expertDialog: {
        show: false,
        expert: null,
      },
      declineDialog: {
        show: false,
        id: null,
      },
      conferenceLinkDialog: {
        show: false,
        id: null,
        url: '',
      },
      linkDialog: {
        show: false,
        kind: 'expert',
        link: null,
        mainId: null, // эксперт / тип услуги, из карточки которого создаётся ссылка
      },
      disableLinkDialog: {
        show: false,
        kind: null,
        id: null,
      },
      linkCopied: false,
      appointmentsPreset: null, // фильтр вкладки «Записи» при переходе из статистики
      changeExpertDone: 0, // растёт после успешной смены эксперта — список закрывает диалог
    };
  },
  computed: {
    ...mapGetters('calendar', [
      'experts',
      'serviceTypes',
      'services',
      'loading',
      'errors',
      'notification',
      'currentMonth',
      'selectedDate',
      'availabilityDialog',
      'availabilityByDate',
      'availabilityForMonth',
      'appointments',
      'appointmentsByDate',
      'cancelledAppointmentsByDate',
      'appointmentsForMonth',
      'openAvailability',
      'servicesByExpert',
      'expertPublicLinks',
      'servicePublicLinks',
      'publicLinksForbidden',
    ]),
    ...mapGetters('auth', ['user']),
    // Права на ссылки (окончательно решает бэк): админ — все ссылки; эксперт календаря
    // (эксперт, привязанный к текущему пользователю через поле user) — ссылки на себя и на
    // типы услуг, которые он оказывает через активную услугу.
    isAdmin() {
      return Boolean(this.user) && this.user.user_type === 'Admin';
    },
    myExpert() {
      if (!this.user) return null;
      return this.experts.find((expert) => expert.user === this.user.user && expert.is_active !== false) || null;
    },
    // Ссылки показываем всем, кому бэк их отдаёт; при 403 блоки ссылок скрыты.
    showLinks() {
      return !this.publicLinksForbidden;
    },
    linkServiceTypeIds() {
      if (!this.myExpert) return [];
      const ids = this.servicesByExpert(this.myExpert.id)
        .filter((item) => item.is_active !== false)
        .map((item) => item.service_type);
      return [...new Set(ids)];
    },
    monthSummary() {
      return this.$store.getters['calendar/monthSummary'](this.currentMonth);
    },
    editingAvailability() {
      if (!this.availabilityDialog.id) return null;
      return this.openAvailability.find((item) => item.id === this.availabilityDialog.id) || null;
    },
  },
  created() {
    this.loadCalendarData();
  },
  methods: {
    ...mapActions('calendar', [
      'loadCalendarData',
      'createAvailability',
      'updateAvailability',
      'closeAvailability',
      'acceptAppointment',
      'declineAppointment',
      'setConferenceLink',
      'changeAppointmentExpert',
      'clearChangeExpertErrors',
      'setSelectedDate',
      'setCurrentMonth',
      'openCreateAvailability',
      'openEditAvailability',
      'closeAvailabilityDialog',
      'saveExpertWithServices',
      'deleteExpert',
      'uploadExpertPhoto',
      'deleteExpertPhoto',
      'createServiceType',
      'updateServiceType',
      'setServiceTypeActive',
      'savePublicLink',
      'setPublicLinkActive',
    ]),
    ...mapMutations('calendar', ['SET_NOTIFICATION']),
    // Переход из статистики шапки: записи выбранного месяца с нужным статусом.
    showAppointments({ status, appointmentId }) {
      this.appointmentsPreset = {
        status, appointmentId, month: this.currentMonth, nonce: Date.now(),
      };
      this.tab = 3;
    },
    // Переход из панели эксперта: его предстоящие записи (или конкретная запись).
    // Счётчик «Ждут подтверждения» не включает просроченные — поэтому тоже период «Предстоящие».
    showExpertAppointments({ expertId, status, appointmentId }) {
      this.appointmentsPreset = {
        status, appointmentId, expertId, period: 'upcoming', nonce: Date.now(),
      };
      this.tab = 3;
    },
    openDate(date) {
      this.setCurrentMonth(date.slice(0, 7));
      this.setSelectedDate(date);
      this.tab = 0;
    },
    async onSaveAvailability(payload) {
      if (payload.id) {
        await this.updateAvailability(payload);
      } else {
        await this.createAvailability(payload);
      }
      this.closeAvailabilityDialog();
    },
    async onCloseAvailability(id) {
      await this.closeAvailability(id);
    },
    async onAcceptAppointment(payload) {
      await this.acceptAppointment(payload);
    },
    onDeclineAppointment(id) {
      this.declineDialog = { show: true, id };
    },
    async onConfirmDecline() {
      await this.declineAppointment(this.declineDialog.id);
      this.declineDialog.show = false;
    },
    onEditConferenceLink(appointment) {
      this.conferenceLinkDialog = { show: true, id: appointment.id, url: appointment.conference_url || '' };
    },
    async onSaveConferenceLink(url) {
      await this.setConferenceLink({ id: this.conferenceLinkDialog.id, conference_url: url });
      this.conferenceLinkDialog.show = false;
    },
    // Ошибка (400/409/403) остаётся в диалоге смены эксперта — здесь её только гасим.
    async onChangeExpert(payload) {
      try {
        await this.changeAppointmentExpert(payload);
        this.changeExpertDone += 1;
      } catch (err) {
        // показано в диалоге
      }
    },
    openCreateExpert() {
      this.expertDialog = { show: true, expert: null };
    },
    openEditExpert(expert) {
      this.expertDialog = { show: true, expert };
    },
    async onSaveExpert(payload) {
      await this.saveExpertWithServices(payload);
      this.expertDialog.show = false;
    },
    async onDeleteExpert(id) {
      await this.deleteExpert(id);
    },
    async onUploadExpertPhoto(payload) {
      await this.uploadExpertPhoto(payload);
      this.refreshExpertDialog(payload.id);
    },
    async onDeleteExpertPhoto(id) {
      await this.deleteExpertPhoto(id);
      this.refreshExpertDialog(id);
    },
    refreshExpertDialog(expertId) {
      if (!this.expertDialog.expert || this.expertDialog.expert.id !== expertId) return;
      this.expertDialog.expert = this.experts.find((item) => item.id === expertId) || null;
    },
    async onSaveServiceType(payload) {
      if (payload.id) {
        await this.updateServiceType(payload);
      } else {
        await this.createServiceType(payload);
      }
    },
    async onToggleServiceTypeActive(payload) {
      await this.setServiceTypeActive(payload);
    },
    openCreateLink({ kind, mainId }) {
      this.$store.commit('calendar/SET_ERRORS', { key: 'publicLinks', value: [] });
      this.linkDialog = {
        show: true, kind, link: null, mainId,
      };
    },
    openEditLink({ kind, link }) {
      this.$store.commit('calendar/SET_ERRORS', { key: 'publicLinks', value: [] });
      this.linkDialog = {
        show: true, kind, link, mainId: null,
      };
    },
    async copyLink(url) {
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // navigator.clipboard недоступен вне https/localhost — копируем через временное поле.
        const textarea = document.createElement('textarea');
        textarea.value = url;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      this.linkCopied = true;
    },
    async onSaveLink(payload) {
      try {
        await this.savePublicLink(payload);
        this.linkDialog.show = false;
      } catch {
        // ошибки уже в errors.publicLinks — диалог остаётся открытым
      }
    },
    // Отключение — через подтверждение, включение — сразу.
    onToggleLinkActive(payload) {
      if (payload.is_active) {
        this.setLinkActive(payload);
      } else {
        this.disableLinkDialog = { show: true, kind: payload.kind, id: payload.id };
      }
    },
    async onConfirmDisableLink() {
      const { kind, id } = this.disableLinkDialog;
      await this.setLinkActive({ kind, id, is_active: false });
      this.disableLinkDialog.show = false;
    },
    async setLinkActive(payload) {
      try {
        await this.setPublicLinkActive(payload);
      } catch {
        // уведомление об ошибке уже показано
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.calendar-tabs ::v-deep .v-tab {
  text-transform: none;
}
</style>
